// src/components/Pimgy/PigmyWithdraw.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import '../GL/LoanPayment.css';

const PigmyWithdraw = () => {
  const [applications, setApplications] = useState([]);
  const [withdrawals, setWithdrawals] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/pimgy/active-applications`);
      // Show applications that still have withdrawable balance (total + interest)
      const appsWithBalance = response.data.filter((app) => {
        const totalPaid = Math.max(0, Number(app.total_paid || 0));
        const interest = Math.max(0, Number(app.interest || 0));
        const totalWithInterest = Math.max(0, Number(app.total_with_interest || (totalPaid + interest)));
        return totalWithInterest > 0.009;
      });
      setApplications(appsWithBalance);
      
      // Initialize withdrawal amounts
      const withdrawalState = {};
      appsWithBalance.forEach(app => {
        withdrawalState[app.registration_no] = '';
      });
      setWithdrawals(withdrawalState);
    } catch (error) {
      console.error('Error fetching applications:', error);
      alert('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalChange = (registrationNo, value) => {
    setWithdrawals(prev => ({
      ...prev,
      [registrationNo]: value
    }));
  };

  const handleWithdraw = async (registrationNo) => {
    const withdrawalAmount = parseFloat(withdrawals[registrationNo]);
    
    if (!withdrawalAmount || withdrawalAmount <= 0) {
      alert('Please enter a valid withdrawal amount');
      return;
    }

    // Find the application to check withdrawable amount (total + interest)
    const application = applications.find(app => app.registration_no === registrationNo);
    const totalPaid = Number(application?.total_paid || 0);
    const interest = Number(application?.interest || 0);
    const totalWithInterest = Number(application?.total_with_interest || (totalPaid + interest));

    if (withdrawalAmount > totalWithInterest) {
      alert(`Withdrawal amount (₹${withdrawalAmount.toFixed(2)}) cannot exceed total + interest (₹${totalWithInterest.toFixed(2)})`);
      return;
    }

    if (!window.confirm(`Confirm withdrawal of ₹${withdrawalAmount.toFixed(2)} for ${registrationNo}?`)) {
      return;
    }

    try {
      setSubmitting(prev => ({ ...prev, [registrationNo]: true }));
      
      await axios.post(`${API_BASE_URL}/api/pimgy/withdraw`, {
        registration_no: registrationNo,
        withdrawal_amount: withdrawalAmount,
        withdrawal_date: getTodayYYYYMMDD(),
        payment_mode: 'Cash',
        transaction_id: null
      });

      alert('Withdrawal processed successfully!');
      
      // Clear withdrawal amount for this registration
      setWithdrawals(prev => ({
        ...prev,
        [registrationNo]: ''
      }));
      
      // Refresh applications list
      fetchApplications();
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      alert(error.response?.data?.error || 'Failed to process withdrawal');
    } finally {
      setSubmitting(prev => ({ ...prev, [registrationNo]: false }));
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading applications...</div>;
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-form">
        <h1>Pigmy Withdraw</h1>
        
        {applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No applications found with available balance for withdrawal.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Registration No</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Daily Amount</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Total Paid</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Interest @ 14% p.a. (₹)</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Total + Interest (₹)</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Withdraw Amount</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => {
                  const totalPaid = Math.max(0, Number(app.total_paid || 0));
                  const interest = Math.max(0, Number(app.interest || 0));
                  const totalWithInterest = Math.max(0, Number(app.total_with_interest || (totalPaid + interest)));
                  
                  return (
                    <tr key={app.registration_no}>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.registration_no}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.name}</td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                        ₹{Number(app.daily_amount).toFixed(2)}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                        ₹{totalPaid.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                        ₹{interest.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                        ₹{totalWithInterest.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                        <input
                          type="number"
                          value={withdrawals[app.registration_no] || ''}
                          onChange={(e) => handleWithdrawalChange(app.registration_no, e.target.value)}
                          placeholder="Enter amount"
                          max={totalWithInterest}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                          }}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <button
                          onClick={() => handleWithdraw(app.registration_no)}
                          disabled={submitting[app.registration_no]}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: submitting[app.registration_no] ? 'not-allowed' : 'pointer',
                            opacity: submitting[app.registration_no] ? 0.6 : 1
                          }}
                        >
                          {submitting[app.registration_no] ? 'Processing...' : 'Withdraw'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PigmyWithdraw;

